<template>
  <div>
    <e-chart
      v-if="hasData"
      :options="chartOptions"
      :style="getChartStyle"
      autoresize
    >
    </e-chart>
    <div v-else-if="loaded">No Data Available</div>
    <SpinnerCmpt v-else></SpinnerCmpt>
  </div>
</template>

<script>
import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import themeStyle from '@/xvisor/utilities/themeStyle';

export default {
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
  },
  components: {
    SpinnerCmpt,
  },
  mounted() {
    this.httpGet();
  },
  watch: {
    timeRange() {
      this.httpGet();
    },
  },
  data() {
    return {
      loaded: false,
      data: null,
      graphInfo: {
        data: [],
        links: [],
      },
      uniqueZones: {},
      maxNodeCount: 0,
      typesToColors: {
        primary: colorPaletteShade.blue5,
        service: colorPaletteShade.green5,
        zone: colorPaletteShade.purple6,
      },
    };
  },
  computed: {
    getChartStyle() {
      return {
        width: '100% !important',
        height: `${this.getMaxChartHeight}px !important`,
      };
    },
    getMaxChartHeight() {
      const maxHeight = 100 * this.maxNodeCount;
      return maxHeight < 400 ? 400 : maxHeight;
    },
    getUrl() {
      return `/apps/${parseInt(this.$route.params.id, 10)}/cmpts/overviewgraph.json`;
    },
    getHttpParams() {
      return {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
        },
      };
    },
    hasData() {
      return this.loaded && this.graphInfo.data.length;
    },
    chartOptions() {
      return {
        tooltip: {},
        legend: {
          selectedMode: false,
          itemWidth: 15,
          itemHeight: 15,
          data: [
            { name: 'Zones', icon: 'rect' },
            { name: 'Service Components', icon: 'circle' },
            { name: 'Primary Components', icon: 'circle' },
          ],
          textStyle: {
            color: themeStyle.styleToggle(colorPalette.black, colorPalette.white),
          },
        },
        series: [
          {
            type: 'graph',
            layout: 'none',
            categories: [
              {
                name: 'Zones',
                symbol: 'rect',
                itemStyle: {
                  color: colorPaletteShade.purple6,
                },
              },
              {
                name: 'Service Components',
                symbol: 'circle',
                itemStyle: {
                  color: colorPaletteShade.green5,
                },
              },
              {
                name: 'Primary Components',
                symbol: 'circle',
                itemStyle: {
                  color: colorPaletteShade.blue5,
                },
              },
            ],
            label: {
              show: true,
              position: 'bottom',
              color: 'white',
            },
            labelLayout: {
              hideOverlap: true,
            },
            symbolSize: 40,
            symbol: (value, params) => {
              if (params.data.type === 'zone') {
                return 'rect';
              }
              return 'circle';
            },
            itemStyle: {
              color: (params) => this.typesToColors[params.data.type],
              borderColor: 'white',
              borderWidth: 5,
              borderType: 'solid',
            },
            data: this.graphInfo.data,
            links: this.graphInfo.links,
          },
        ],
      };
    },
  },
  methods: {
    getZoneTooltip() {
      return {
        show: true,
        backgroundColor: themeStyle.styleToggle(colorPalette.white, colorPalette.black),
        formatter: (params) => `
          <div class="apache-echarts-tooltip">
            User Count: ${params.data.numUsers}<br>
            Server Count: ${params.data.numServers}<br>
          </div>
        `,
      };
    },
    getComponentTooltip() {
      return {
        show: true,
        backgroundColor: themeStyle.styleToggle(colorPalette.white, colorPalette.black),
        formatter: (params) => `
          <div class="apache-echarts-tooltip">
            User Count: ${params.data.numUsers}<br>
            Server Count: ${params.data.numServers}<br>
            Zone Count: ${params.data.numZones}<br>
          </div>
        `,
      };
    },
    processGraphData() {
      let count = 0;
      this.data.forEach((item, idx) => {
        this.graphInfo.data.push({
          name: item.name,
          x: 100,
          y: 10 * idx,
          type: item.type,
          numUsers: item.numUsers,
          numServers: item.numServers,
          numZones: item.numZones,
          tooltip: this.getComponentTooltip(),
        });
        count += 1;
      });
      this.maxNodeCount = Math.max(this.maxNodeCount, count);
    },
    processGraphLinks() {
      this.uniqueZones = {};
      let count = 0;
      this.data.forEach((item) => {
        item.zones.forEach((element, idx) => {
          this.graphInfo.links.push({
            source: element.name,
            target: item.name,
            label: {
              show: true,
              formatter: () => `${element.numUsers} User(s)`,
            },
          });
          if (!(element.id in this.uniqueZones)) {
            this.graphInfo.data.push({
              name: element.name,
              x: 0,
              y: 10 * idx,
              type: 'zone',
              numUsers: element.numUsers,
              numServers: element.numServers,
              tooltip: this.getZoneTooltip(),
            });
            this.uniqueZones[element.id] = element;
            count += 1;
          }
        });
      });
      this.maxNodeCount = Math.max(this.maxNodeCount, count);
    },
    processGraphUnconnectedComponents() {
      // Create connections for components with no zones.
      this.data.forEach((item) => {
        if (!(item.zones.length)) {
          Object.keys(this.uniqueZones).forEach((key) => {
            this.graphInfo.links.push({
              source: this.uniqueZones[key].name,
              target: item.name,
              label: {
                show: true,
                formatter: () => `${this.uniqueZones[key].numUsers} User(s)`,
              },
              lineStyle: {
                type: [2, 2],
              },
            });
          });
        }
      });
    },
    httpGet() {
      this.graphInfo = {
        data: [],
        links: [],
      };
      this.loaded = false;
      this.maxNodeCount = 0;
      this.$http
        .get(this.getUrl, this.getHttpParams)
        .then((response) => {
          if (response.data) {
            this.data = response.data;
            this.processGraphData();
            this.processGraphLinks();
            this.processGraphUnconnectedComponents();
          }
        })
        .finally(() => { this.loaded = true; });
    },
  },
};
</script>
